import '../ActivityActions.scss';
import React, { useCallback, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { IDocumentData } from '@models/document/IDocumentData';
import { ActivityExecutionService } from '@services/actions/ActivityExecutionService';
import { DocumentService } from '@services/DocumentService';
import { getFormDataByScheme } from '@utils/documentUtils';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { IMailRemindersOptions } from '@molecules/AddMailReminderBlock/AddMailReminderBlock';
import ModalFormBuilder, { IModalFormBuilderProps } from '@molecules/ModalFormBuilder/ModalFormBuilder';
import {
    batchEditingUploadProgress,
    checkBatchEditingSubmit,
    createBatchEditingMailReminders,
} from '@organisms/actions/batchEditing/batchEditingSubmit';
import { useBatchEditingFormData } from '@organisms/actions/batchEditing/useBatchEditingFormData';
import { IActivityBatchEditingProps } from './IActivityBatchEditingProps';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

export const ActivityBatchEditingModal: React.FC<IActivityBatchEditingProps> = (props: IActivityBatchEditingProps) => {
    const [errorText, setErrorText] = useState<string>();
    const [warningText, setWarningText] = useState<string>();
    const [showOkButton, setShowOkButton] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>();
    const [uploadProgress, setUploadProgress] = useState<number>();
    const [submitButtonLabel, setSubmitButtonLabel] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [hasFiles, setHasFiles] = useState<boolean>(false);

    const methods = useForm<IDocumentData>({
        mode: 'onBlur', // "onChange"
    });
    const hasEditedFields = useUnsavedFormInput(methods);
    const service = new ActivityExecutionService();

    const { form, fieldsData, attachesCreateModel, mailRemindersBlockModel } = useBatchEditingFormData({
        docId: props.docId,
        actionKey: props.actionKey,
        activityId: props.actId,
        attachFiles: props.attachFiles,
        addMailReminders: props.addMailReminders,
        showCurrentValues: props.showCurrentValues,
        virtualFields: props.virtualFields,
        methods: methods,
        getFormBuilderScheme: () => service.getFormBuilderScheme(props.actionKey),
        getDataByAction: () => DocumentService.getDataByAction(props.actionKey, props.docId, undefined, true),
        onError: setErrorText,
    });

    const onSubmit = useCallback(
        async (data: FieldValues, files?: ISelectedFiles, mailReminderOptions?: IMailRemindersOptions) => {
            setErrorText(undefined);
            setWarningText(undefined);
            setLoading(true);

            const check = await checkBatchEditingSubmit({
                files: files,
                attachFiles: props.attachFiles,
                attachRequired: props.attachRequired,
                attachRequiredValidation: props.attachRequiredValidation,
                form: form,
                fieldsData: fieldsData,
                methods: methods,
            });
            if (check.warning || check.invalid) {
                if (check.warning) setWarningText(check.warning);
                setLoading(false);
                return;
            }

            const val = getFormDataByScheme(form?.view?.rows?.row!, data, props.onlyEditValues, form?.changeActions);
            const hasFilesToUpload = !!files?.files?.length;
            setIsSubmitting(hasFilesToUpload);
            setSubmitButtonLabel(hasFilesToUpload ? 'Подготавливаю файлы...' : undefined);

            if (hasFilesToUpload) {
                setUploadProgress(0);
            }

            // Сохранение данных
            service
                .saveBatchEditingData(
                    props.docId,
                    props.actionKey,
                    props.actId,
                    val,
                    files,
                    hasFilesToUpload ? batchEditingUploadProgress(setUploadProgress, setSubmitButtonLabel) : undefined,
                )
                .then(async (result) => {
                    if (result.data.warnings && result.data.warnings.length > 0) {
                        setWarningText(result.data.warnings.join(', '));
                        setShowOkButton(false);
                        return;
                    }

                    // Создание почтовых напоминаний только после успешного сохранения
                    if (mailReminderOptions && mailRemindersBlockModel) {
                        await createBatchEditingMailReminders(
                            props.docId,
                            props.actionKey,
                            val,
                            mailRemindersBlockModel,
                            mailReminderOptions,
                        );
                    }

                    props.completeHandler && props.completeHandler(true);
                })
                .catch((error) => setErrorText(error))
                .finally(() => {
                    setLoading(false);
                    setIsSubmitting(false);
                    setUploadProgress(undefined);
                    setSubmitButtonLabel(undefined);
                });
        },
        [form, fieldsData, methods],
    );

    const messageBoxProp: IModalFormBuilderProps = {
        header: props.displayName,
        showOkButton: !!fieldsData && showOkButton,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        size: props.modalSize,
        loading: (!!loading && !isSubmitting) || (!fieldsData && !errorText),
        isSubmitting: isSubmitting,
        // `loading` — всё сохранение (проверки, запись, напоминания); загрузку формы можно бросить.
        closeDisabled: !!loading,
        hasUnsavedInput: () => hasFiles || hasEditedFields(),
        onFilesChanged: (files) => setHasFiles(files.files.length > 0),
        onSubmit: onSubmit,
        onCancel: () => props.completeHandler?.(false),
        errorText: errorText,
        warningText: warningText,
        formMethods: methods,
        fields: fieldsData ?? {},
        changeActions: form?.changeActions,
        rows: form?.view,
        createAttachModel: attachesCreateModel,
        mailRemindersBlockModel: mailRemindersBlockModel,
        showContent: !!fieldsData && showOkButton,
        docId: props.docId,
        submitProgress: uploadProgress,
        submitProgressLabel: submitButtonLabel,
    };

    return <ModalFormBuilder {...messageBoxProp} />;
};
